import Modal from './modal.js';
import countries from 'i18n-iso-countries';
import enCountryLocale from 'i18n-iso-countries/langs/en.json';
import { createDebugger } from '../utils/debug.js';

const debug = createDebugger('geolocation-modal', false);

countries.registerLocale(enCountryLocale);

const SELECTORS = {
    CONTENT_TEMPLATE: '[data-content-template]',
    COUNTRY_WITH_FLAG_TEMPLATE: '[data-country-with-flag-template]',
    URL_TEMPLATE: '[data-url-template]',
    OPTION_DATA: '[data-redirect-option-data]',
    ACCEPT_BUTTON: '[data-geo-accept]'
};

class GeolocationModal extends Modal {
    constructor() {
        super();
    }

    connectedCallback() {
        super.connectedCallback();
        this.init();
    }

    init() {
        debug.log('init');

        const forcedCountry = new URLSearchParams(window.location.search).get('geo_country');
        if (!forcedCountry && sessionStorage.getItem('geolocation-modal-closed')) {
            debug.log('init: geolocation-modal-closed');
            return;
        }

        this.getGeolocationOptionData();

        this.shopCountryRedirectOption = this.getCurrentRedirectOption();

        document.addEventListener(
            'geolocation-recommendation',
            this.handleGeolocationRecommendation.bind(this)
        );
    }

    getGeolocationOptionData() {
        debug.log('getGeolocationOptionData');

        try {
            const redirectOptionData = this.querySelector(SELECTORS.OPTION_DATA);
            if (!redirectOptionData) {
                throw new Error(
                    `Missing geolocation data script (${SELECTORS.OPTION_DATA}) in geoloaction modal`
                );
            }

            const json = JSON.parse(redirectOptionData.textContent);

            this.redirectOptions = json.redirect_options;

            debug.log('this.redirectOptions', this.redirectOptions);

            if (!this.redirectOptions) {
                throw new Error('Missing redirect options in geoloaction data');
            }

            redirectOptionData.remove();
        } catch (error) {
            throw new Error(`Error getting geoloaction data: ${error}`, this);
        }
    }

    getRedirectOption(country) {
        const getPotentialRedirect = (redirectOptions) => {
            debug.log('getRedirectOption', country);
            debug.log('redirectOptions', redirectOptions);
            const redirectOptionByCountry = redirectOptions.find((redirectOption) =>
                redirectOption.countries.includes(country)
            );

            if (redirectOptionByCountry) {
                return redirectOptionByCountry;
            }

            return redirectOptions.find((redirectOption) => redirectOption.is_default);
        };

        const redirectOption = getPotentialRedirect(this.redirectOptions);

        if (this.shopCountryRedirectOption && this.shopCountryRedirectOption.name === redirectOption.name) {
            return null;
        }

        return redirectOption;
    }

    getCurrentRedirectOption() {
        let currentOrigin = window.location.origin + window.Shopify.routes.root;

        if (currentOrigin.endsWith('/')) {
            currentOrigin = currentOrigin.slice(0, -1);
        }

        const redirectOption = this.redirectOptions.find((redirectOption) => {
            const originMatch = redirectOption.redirect_url.includes(currentOrigin);
            debug.log('getCurrentRedirectOption', {
                currentOrigin,
                redirectUrl: redirectOption.redirect_url,
                originMatch
            });

            return originMatch;
        });
        return redirectOption;
    }

    populateTemplate({ customerCountry, shopCountry, redirectOption }) {
        const contentTemplate =
            this.querySelector(`[data-content-template][data-redirect-url="${redirectOption.redirect_url}"]`) ||
            this.querySelector('[data-content-template][data-is-default]');

        if (!contentTemplate) {
            throw new Error(`Missing content template for redirect option: ${redirectOption.redirect_url}`);
        }

        const contentString = contentTemplate.innerHTML
            .replaceAll('[customer_country]', customerCountry)
            .replaceAll('[shop_country]', shopCountry)
            .replaceAll('[shop_country_w_flag]', this.getCountryHTMlWithFlag(this.shopCountryRedirectOption))
            .replaceAll('[redirect_name]', redirectOption.name)
            .replaceAll('[redirect_name_w_flag]', this.getCountryHTMlWithFlag(redirectOption))
            .replaceAll('[redirect_url]', this.getUrlTemplate(redirectOption));

        debug.log('contentString', contentString);

        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = contentString;
        contentTemplate.parentNode.insertBefore(tempDiv.firstElementChild, contentTemplate);

        this.querySelectorAll(SELECTORS.CONTENT_TEMPLATE).forEach((t) => t.remove());
    }

    getCountryHTMlWithFlag(redirectOption) {
        if (!redirectOption.image_url) {
            return redirectOption.name; // Return just the name if no image
        }

        if (!this.countryWithFlagTemplate) {
            this.countryWithFlagTemplate = this.querySelector(SELECTORS.COUNTRY_WITH_FLAG_TEMPLATE);
            if (!this.countryWithFlagTemplate) {
                throw new Error(
                    `Missing country with flag template (${SELECTORS.COUNTRY_WITH_FLAG_TEMPLATE}s) in geoloaction modal`
                );
            }
        }

        const countryWithFlagTemplate = this.countryWithFlagTemplate.innerHTML
            .replaceAll('[image_url]', redirectOption.image_url || '')
            .replaceAll('[name]', redirectOption.name || 'Unknown');

        this.countryWithFlagTemplate.remove();

        return countryWithFlagTemplate;
    }

    getUrlTemplate(redirectOption) {
        const { redirect_url: url, redirect_path: path } = redirectOption;
        const urlObj = new URL(url);
        const urlClean = urlObj.origin.replace('https://', '').replace('http://', '');

        if (!this.urlTemplate) {
            this.urlTemplate = this.querySelector(SELECTORS.URL_TEMPLATE);
            if (!this.urlTemplate) {
                return path; // Fallback to plain URL if template not found
            }
        }

        debug.log('getUrlTemplate', { url, path, urlClean });

        // The visible text stays the destination store, but the link goes through the
        // local /go/ redirect so Shopify's exit interstitial never sees a cross-origin
        // target. See snippets/market-url.liquid.
        const urlTemplate = this.urlTemplate.innerHTML
            .replaceAll('[url]', path)
            .replaceAll('[url_clean]', urlClean);

        return urlTemplate;
    }

    getCountryFromISO(isoCode) {
        if (!isoCode) {
            throw new Error(`Invalid ISO code: ${isoCode}`);
        }

        return countries.getName(isoCode, 'en');
    }

    handleGeolocationRecommendation(event) {
        debug.log('handleGeolocationRecommendation', event);

        this.recommendedCountry = event.detail.recommendedCountry;

        const redirectOption = this.getRedirectOption(this.recommendedCountry);
        debug.log('redirectOption', redirectOption);
        if (redirectOption) {
            this.populateTemplate({
                customerCountry: this.getCountryFromISO(this.recommendedCountry),
                shopCountry: this.shopCountryRedirectOption.name,
                redirectOption: redirectOption
            });

            debug.log('recommendedCountry', this.recommendedCountry);

            this.delegateClick(SELECTORS.ACCEPT_BUTTON, (match, event) => {
                this.handleAcceptButtonClick({
                    redirectOption
                });
            });

            this.open();
            debug.log('this', this);
        }
    }

    handleAcceptButtonClick({ redirectOption }) {
        try {
            debug.log('handleAcceptButtonClick', redirectOption);
            const { pathname, searchParams } = this.getCurrentPathAndParams();

            // Stays on this origin so Shopify's exit interstitial lets it through. The
            // /go/ redirect forwards the query string but drops any extra path, so the
            // page to land on travels as tr_path. See snippets/market-url.liquid.
            const redirectUrl = new URL(redirectOption.redirect_path, window.location.origin);

            if (pathname) {
                const landingPath = pathname.startsWith('/') ? pathname : `/${pathname}`;
                redirectUrl.searchParams.set('tr_path', landingPath);
            }

            // Add existing search params from current URL
            searchParams.forEach((value, key) => {
                redirectUrl.searchParams.set(key, value);
            });

            // Add country parameter
            if (this.recommendedCountry) {
                redirectUrl.searchParams.set('country', this.recommendedCountry);
            }

            window.location.href = redirectUrl.toString();
        } catch (error) {
            debug.error('handleAcceptButtonClick', error);
            window.location.href = redirectOption.redirect_path;
        }
    }

    getCurrentPathAndParams() {
        const rootpath = window.Shopify.routes.root;
        const pathname = window.location.pathname.replace(rootpath, '');
        const searchParams = new URLSearchParams(window.location.search);

        return { pathname, searchParams };
    }

    onClose() {
        sessionStorage.setItem('geolocation-modal-closed', 'true');
    }

    onThemeEditorLoad() {
        this.populateTemplate({
            customerCountry: this.getCountryFromISO('LT'),
            shopCountry: this.shopCountryRedirectOption.name,
            redirectOption: this.redirectOptions.find((redirectOption) => redirectOption.is_default)
        });
    }
}

customElements.define('geolocation-modal', GeolocationModal);
